import { RoutalSelect } from '@nx-smartmonkey/ui';
import React, { useState } from 'react';

import { ContextualMenu } from '../../../../../components/menus/ContextualMenu';
import { Organization } from '../../../../../domain/backoffice/organization/Organization';
import Text from '../../../../../react-components/Text';
import { BackofficeClientsOrganizationsManageGeocodeDialog } from './BackofficeClientsOrganizationsManageGeocodeDialog';
import { BackofficeClientsOrganizationsManageSMSDialog } from './BackofficeClientsOrganizationsManageSMSDialog';

type BackofficeClientsOrganizationActionsMenuContextAnchor = {
  screenPosition: {
    top: number;
    left: number;
  };
};

type BackofficeClientsOrganizationActionsMenuType = `contextual` | `popover`;

type BackofficeClientsOrganizationActionsMenuProps = {
  organization: Organization;
  type?: BackofficeClientsOrganizationActionsMenuType;
  showManageSMS?: boolean;
  showManageGeocodeCredit?: boolean;
  contextAnchor?: BackofficeClientsOrganizationActionsMenuContextAnchor;
  popoverProps?: {
    hideArrow?: boolean;
    style?: React.CSSProperties;
    containerStyle?: React.CSSProperties;
    content?: React.ReactElement;
  };
  onManageSMS?: () => void;
  onManageGeocodeCredit?: () => void;
  setContextAnchor?: (context?: BackofficeClientsOrganizationActionsMenuContextAnchor) => void;
};

export const BackofficeClientsOrganizationActionsMenu = ({
  organization,
  type = `contextual` as BackofficeClientsOrganizationActionsMenuType,
  contextAnchor = {
    screenPosition: {
      top: 0,
      left: 0,
    },
  },
  showManageSMS = true,
  showManageGeocodeCredit = true,
  popoverProps,
  onManageSMS,
  onManageGeocodeCredit,
  setContextAnchor = () => {},
}: BackofficeClientsOrganizationActionsMenuProps) => {
  const [dialog, setDialog] = useState<`manage_sms_limit` | `manage_geocode_credit` | undefined>(undefined);

  if (!organization) return null;

  return (
    <>
      {type === `popover` && (
        <RoutalSelect
          id="routes-select-button-popover"
          hideArrow={popoverProps?.hideArrow}
          style={{
            background: `transparent`,
            ...(popoverProps?.style ?? {}),
          }}
          containerStyle={popoverProps?.containerStyle}
          popoverProps={{
            maxHeight: `unset`,
            options: [
              ...(!showManageSMS
                ? []
                : [
                    {
                      key: `manage_organization_sms`,
                      width: 240,
                      onClick: () => {
                        setDialog(`manage_sms_limit`);
                      },
                      content: <Text intlId="administration.users.manage_sms.title" style={{ cursor: `pointer` }} />,
                    },
                  ]),
              ...(!showManageGeocodeCredit
                ? []
                : [
                    {
                      key: `manage_organization_geocode_credit`,
                      width: 240,
                      onClick: () => {
                        setDialog(`manage_geocode_credit`);
                      },
                      content: (
                        <Text intlId="administration.users.manage_geocode_credit.title" style={{ cursor: `pointer` }} />
                      ),
                    },
                  ]),
            ],
          }}
        >
          {popoverProps?.content}
        </RoutalSelect>
      )}
      {type === `contextual` && (
        <>
          {contextAnchor ? (
            <ContextualMenu
              styledMenuItemStyles={{
                width: `240px`,
              }}
              anchorElementPosition={contextAnchor.screenPosition}
              onClose={() => {
                if (setContextAnchor) {
                  setContextAnchor();
                }
              }}
              options={[
                ...(!showManageSMS
                  ? []
                  : [
                      {
                        key: `manage_organization_sms`,
                        text: <Text intlId="administration.users.manage_sms.title" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`manage_sms_limit`);
                        },
                      },
                    ]),
                ...(!showManageGeocodeCredit
                  ? []
                  : [
                      {
                        key: `manage_organization_geocode_credit`,
                        text: (
                          <Text
                            intlId="administration.users.manage_geocode_credit.title"
                            style={{ cursor: `pointer` }}
                          />
                        ),
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`manage_geocode_credit`);
                        },
                      },
                    ]),
              ]}
            />
          ) : null}
        </>
      )}
      {dialog === `manage_sms_limit` ? (
        <BackofficeClientsOrganizationsManageSMSDialog
          openDialog={dialog === `manage_sms_limit`}
          organizationId={organization.id}
          subscription={organization.subscription}
          onClose={(hasBeenUpdated: boolean) => {
            setDialog(undefined);

            if (setContextAnchor) {
              setContextAnchor();
            }

            if (hasBeenUpdated && onManageSMS) {
              onManageSMS();
            }
          }}
        />
      ) : null}
      {dialog === `manage_geocode_credit` ? (
        <BackofficeClientsOrganizationsManageGeocodeDialog
          openDialog={dialog === `manage_geocode_credit`}
          organizationId={organization.id}
          subscription={organization.subscription}
          onClose={(hasBeenUpdated: boolean) => {
            setDialog(undefined);

            if (setContextAnchor) {
              setContextAnchor();
            }

            if (hasBeenUpdated && onManageGeocodeCredit) {
              onManageGeocodeCredit();
            }
          }}
        />
      ) : null}
    </>
  );
};
